<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>
    <template v-slot:default>
      <main class="bg-gray-50/40">
        <!-- ===== Hero (admin-configurable via PageHero) ===== -->
        <PageHero page="profil" :title="page.props.apps?.namaapps" current="Profil"
          subtitle="Fakultas Ekonomi dan Bisnis · Universitas Negeri Gorontalo" />

        <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
          <!-- ===== Tentang ===== -->
          <section v-if="p.tentang" class="py-12 lg:py-16">
            <div class="grid items-start gap-10 lg:grid-cols-[1fr_360px]">
              <div>
                <span
                  class="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-semibold tracking-wide uppercase rounded-full text-primary bg-primary/10">
                  <span class="inline-block w-1.5 h-1.5 rounded-full bg-primary"></span>
                  Tentang
                </span>
                <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl font-roboto-slab">
                  Tentang Jurusan
                </h2>
                <div class="mt-5 leading-relaxed prose text-gray-600 prose-gray max-w-none" v-html="p.tentang"></div>
              </div>
              <div class="lg:sticky lg:top-28">
                <img :src="heroImage" alt="Gedung UNG"
                  class="object-cover w-full shadow-lg h-60 lg:h-72 rounded-2xl ring-1 ring-gray-100" />
              </div>
            </div>
          </section>

          <!-- ===== Visi & Misi ===== -->
          <section v-if="p.visi || p.misi" class="py-12 lg:py-16 border-t border-gray-100">
            <div class="max-w-2xl mb-10">
              <span
                class="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-semibold tracking-wide uppercase rounded-full text-primary bg-primary/10">
                <span class="inline-block w-1.5 h-1.5 rounded-full bg-primary"></span>
                Arah & Tujuan
              </span>
              <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl font-roboto-slab">
                Visi &amp; Misi
              </h2>
            </div>
            <div class="grid gap-6 md:grid-cols-2">
              <article v-if="p.visi"
                class="relative p-7 overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
                <div
                  class="inline-flex items-center justify-center mb-5 text-white rounded-xl w-11 h-11 bg-primary">
                  <i class="fa-solid fa-eye"></i>
                </div>
                <h3 class="mb-3 text-lg font-bold text-gray-900 font-roboto-slab">Visi</h3>
                <div class="leading-relaxed prose text-gray-600 prose-gray max-w-none" v-html="p.visi"></div>
              </article>
              <article v-if="p.misi"
                class="relative p-7 overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
                <div
                  class="inline-flex items-center justify-center mb-5 text-white rounded-xl w-11 h-11 bg-primary">
                  <i class="fa-solid fa-list-check"></i>
                </div>
                <h3 class="mb-3 text-lg font-bold text-gray-900 font-roboto-slab">Misi</h3>
                <div class="leading-relaxed prose text-gray-600 prose-gray max-w-none" v-html="p.misi"></div>
              </article>
            </div>
          </section>

          <!-- ===== Akreditasi ===== -->
          <section v-if="p.akreditasi" class="py-12 lg:py-16 border-t border-gray-100">
            <div class="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-2xl">
              <div class="grid md:grid-cols-[280px_1fr]">
                <div class="flex flex-col items-center justify-center gap-3 p-8 text-center text-white bg-gradient-to-br from-primary to-primary/80">
                  <i class="text-4xl fa-solid fa-award"></i>
                  <span class="text-lg font-bold font-roboto-slab">Akreditasi</span>
                  <span class="text-sm text-white/80">Jaminan mutu program studi</span>
                </div>
                <div class="p-7 lg:p-8">
                  <div class="leading-relaxed prose text-gray-600 prose-gray max-w-none" v-html="p.akreditasi"></div>
                </div>
              </div>
            </div>
          </section>

          <!-- ===== Pimpinan Jurusan ===== -->
          <section v-if="formasi?.data?.length" class="py-8 lg:py-12 border-t border-gray-100">
            <ProfilPejabat :pejabatList="formasi" />
          </section>

          <!-- ===== Staff Jurusan ===== -->
          <section v-if="pegawai?.data?.length" class="py-8 border-t border-gray-100 lg:py-12">
            <ProfilPegawai :pegawaiList="pegawai" />
          </section>

          <!-- Fallback when no profil data -->
          <section v-if="!p.tentang && !p.visi && !p.misi && !p.akreditasi" class="py-20 text-center">
            <p class="text-gray-400">Data profil belum tersedia.</p>
          </section>
        </div>

        <div class="h-12"></div>
      </main>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import PageHero from "@/Components/Web/PageHero.vue";
import ProfilPejabat from "@/Components/Web/ProfilPejabat.vue";
import ProfilPegawai from "@/Components/Web/ProfilPegawai.vue";

const page = usePage();

// Public asset (served from /storage, not bundled by Vite).
const heroImage = "/storage/images/BGUNG.png";

const props = defineProps({
  profil: {
    type: Object,
    required: true,
  },
  formasi: {
    type: Object,
    default: () => ({ data: [] }),
  },
  pegawai: {
    type: Object,
    default: () => ({ data: [] }),
  },
});

// First (and only) profil row; empty object when absent.
const p = computed(() => props.profil?.data?.[0] || {});
</script>

<style scoped>
/* Ordered/unordered lists inside v-html (visi/misi/akreditasi). Scoped to
   .prose so they only affect injected HTML, not slotted components. */
.prose :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin: 0.75rem 0;
}

.prose :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 0.75rem 0;
}

.prose :deep(li) {
  margin-bottom: 0.5rem;
  line-height: 1.7;
}

.prose :deep(p) {
  margin: 0.5rem 0;
}

.prose :deep(strong) {
  font-weight: 600;
  color: #1f2937;
}
</style>
